<mat-card class="badge-detail-card">

  <!-- Close button (floating in top-right) -->
  <button
    type="button"
    mat-icon-button
    class="badge-detail-close"
    (click)="closePopover.emit()">
    <mat-icon>close</mat-icon>
  </button>

  <!-- Scrollable Content Area -->
  <zef-scrollbar [maxHeight]="'calc(100vh - 100px)'">
    <div class="badge-detail-content">

      <!-- Mode-based content rendering -->
      @switch (mode()) {

        <!-- DNS Badge Popover -->
        @case ('dns') {
          <div class="__badge-detail-section">

            <!-- DNS Status as Title (SSL-style with icon) -->
            <div
              class="__badge-detail-section-header __badge-detail-section-header--dns"
              [class.is-error]="domain().dnsCheckStatus === dnsStatuses.Failed"
              [class.is-processing]="domain().dnsCheckStatus === dnsStatuses.Pending || domain().dnsCheckStatus === dnsStatuses.Checking"
              [class.is-active]="domain().dnsCheckStatus === dnsStatuses.Ok">
              <mat-icon class="__badge-detail-section-icon">
                {{ domain().dnsCheckStatus === dnsStatuses.Ok ? 'check_circle' : (domain().dnsCheckStatus === dnsStatuses.Failed ? 'error' : 'schedule') }}
              </mat-icon>
              <span class="__badge-detail-section-title">{{ translations().dnsSectionTitles[domain().dnsCheckStatus] }}</span>
            </div>

            <!-- DNS Status Description - only for Failed -->
            @if (domain().dnsCheckStatus === dnsStatuses.Failed) {
              <p class="__badge-detail-info __badge-detail-info--warning" [innerHTML]="translations().dnsStatuses[domain().dnsCheckStatus]"></p>
            }

            <!-- Check Times (Next and Last on ONE line) -->
            <div class="__badge-detail-checks-row">
              <div class="__badge-detail-check-item">
                <span class="__badge-detail-check-label">
                  {{ translations().detailLabels.nextCheckAt }}
                </span>
                <span
                  class="__badge-detail-check-value"
                  [matTooltip]="domain().nextDnsCheckAt | date:'medium'"
                  [matTooltipClass]="'c-zerops-tooltip'"
                  [matTooltipPosition]="'above'">
                  <zui-relative-time
                    [dateTime]="domain().nextDnsCheckAt"
                    [isFuture]="true" />
                </span>
              </div>
              <div class="__badge-detail-check-item">
                <span class="__badge-detail-check-label">
                  {{ translations().detailLabels.lastDnsCheckAt }}
                </span>
                <span
                  class="__badge-detail-check-value"
                  [matTooltip]="domain().lastDnsCheckAt | date:'medium'"
                  [matTooltipClass]="'c-zerops-tooltip'"
                  [matTooltipPosition]="'above'">
                  <zui-relative-time
                    [dateTime]="domain().lastDnsCheckAt"
                    [isFuture]="false" />
                </span>
              </div>
            </div>

            <!-- Trigger DNS Check Button -->
            <div class="__badge-detail-actions">
              <zef-progress-error [key]="triggerDnsCheckActionKey()">
                <button
                  type="button"
                  mat-stroked-button
                  class="c-button--smaller"
                  (click)="triggerDnsCheckClicked.emit({
                    httpRoutingId: httpRoutingId(),
                    domainName: domain().domainName
                  })">
                  <mat-icon>refresh</mat-icon>
                  {{ translations().detailLabels.triggerDnsCheck }}
                </button>
              </zef-progress-error>
            </div>

            <!-- Separator (hide when status is PENDING) -->
            @if (domain().dnsCheckStatus !== dnsStatuses.Pending) {
              <zui-separator
                [orientation]="'horizontal'"
                [size]="'small'"
                [spacing]="'small'"
                [position]="'start'" />
            }

            <!-- Used Name Servers -->
            @if (domain().lastDnsCheckDetail?.resolvedNameServers?.length) {
              <div class="__badge-detail-subsection">
                <div class="__badge-detail-subsection-title">
                  {{ translations().detailLabels.resolvedNameServers }}
                </div>
                <div class="__badge-detail-tag-list">
                  @for (ns of domain().lastDnsCheckDetail.resolvedNameServers; track ns) {
                    <span class="__badge-detail-tag">{{ ns | middleEllipsis : 40 }}</span>
                  }
                </div>
              </div>
            }

            <!-- Resolved IP Addresses -->
            @if (domain().lastDnsCheckDetail?.resolvedIpAddresses?.length) {
              <div class="__badge-detail-subsection">
                <div class="__badge-detail-subsection-title">
                  {{ translations().detailLabels.resolvedIpAddresses }}
                </div>
                <div class="__badge-detail-tag-list">
                  @for (ip of domain().lastDnsCheckDetail.resolvedIpAddresses; track ip) {
                    <span class="__badge-detail-tag">{{ ip | middleEllipsis : 40 }}</span>
                  }
                </div>
              </div>
            }

            <!-- Challenges -->
            @if (domain().lastDnsCheckDetail?.challenges?.length) {
              <div class="__badge-detail-subsection">
                <div class="__badge-detail-subsection-title">
                  {{ domain().domainName.startsWith('*.') ? translations().detailLabels.dnsChallenges : translations().detailLabels.httpChallenges }}
                </div>
                <div class="__badge-detail-challenge-header">
                  <div class="__badge-detail-challenge-header-text">{{ translations().detailLabels.challengesCalledWithHeader }}</div>
                  <zef-copy-to-clipboard
                    class="__badge-detail-challenge-header-copy"
                    [copyData]="domain().lastDnsCheckDetail.challenges[0].challengeHost"
                    [tooltip]="'Copy to clipboard'">
                    <span class="__badge-detail-challenge-header-code">Host: {{ domain().lastDnsCheckDetail.challenges[0].challengeHost | middleEllipsis : 40 }}</span>
                  </zef-copy-to-clipboard>
                </div>
                @for (challenge of domain().lastDnsCheckDetail.challenges; track challenge.challengeUrl) {
                  <div
                    class="__badge-detail-challenge"
                    [class.is-active]="challenge.httpStatusCode >= 200 && challenge.httpStatusCode < 300"
                    [class.is-warning]="challenge.httpStatusCode >= 400 || challenge.httpStatusCode === 0 || !challenge.httpStatusCode">

                    <!-- Challenge URL with Status on left -->
                    <div class="__challenge-url-row">
                      @if (challenge.httpStatusCode !== null && challenge.httpStatusCode !== undefined) {
                        <span
                          class="__challenge-status"
                          [class.is-error]="challenge.httpStatusCode >= 400 || challenge.httpStatusCode === 0"
                          [class.is-success]="challenge.httpStatusCode >= 200 && challenge.httpStatusCode < 300">
                          {{ challenge.httpStatusCode }}
                        </span>
                      }
                      <zef-copy-to-clipboard
                        class="__challenge-item-copy"
                        [copyData]="challenge.challengeUrl"
                        [tooltip]="'Copy to clipboard'">
                        <span class="__challenge-item __challenge-item--url">
                          <span class="__challenge-item-text">{{ challenge.challengeUrl | middleEllipsis : 40 }}</span>
                        </span>
                      </zef-copy-to-clipboard>
                    </div>

                    @if (challenge.error !== null && challenge.error !== undefined) {
                      <div class="__challenge-error-section">
                        <div class="__challenge-error-label">
                          {{ challenge.error }}
                        </div>
                        @if (challenge.httpResponse !== null
                             && challenge.httpResponse !== undefined
                             && challenge.httpStatusCode !== 200) {
                          <div class="__challenge-error-value">
                            {{ challenge.httpResponse }}
                          </div>
                        }
                      </div>
                    } @else if (challenge.httpResponse !== null
                                && challenge.httpResponse !== undefined
                                && challenge.httpStatusCode !== 200) {
                      <div class="__badge-detail-row">
                        <span class="__badge-detail-value __badge-detail-value--mono">
                          {{ challenge.httpResponse }}
                        </span>
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- No DNS Records Warning (not for wildcard domains, not during active checks) -->
            @if (!domain().lastDnsCheckDetail?.resolvedIpAddresses?.length
                 && !domain().domainName.startsWith('*.')
                 && domain().dnsCheckStatus !== dnsStatuses.Pending
                 && domain().dnsCheckStatus !== dnsStatuses.Checking) {
              <div class="__badge-detail-warning">
                <mat-icon>warning</mat-icon>
                <span>{{ translations().detailLabels.noDnsRecords }}</span>
              </div>
            }

            <!-- Wildcard CNAME Check Error -->
            @if (domain().lastDnsCheckDetail?.wildcardCnameCheckError) {
              <div class="__badge-detail-warning">
                <mat-icon>warning</mat-icon>
                <span>{{ domain().lastDnsCheckDetail.wildcardCnameCheckError }}</span>
              </div>
            }

          </div>
        }

        <!-- SSL Badge Popover (Combined Installation + Deployment) -->
        @case ('ssl') {
          <div class="__badge-detail-section">

            <!-- SSL Installation Section (ABOVE deployment) -->
            @if (showSslInstallation()) {
              <div class="__ssl-installation-section">
                <div
                  class="__badge-detail-section-header __badge-detail-section-header--ssl"
                  [class.is-waiting]="domain().sslStatus === sslStatuses.WaitingForDns">
                  <mat-icon class="__badge-detail-section-icon">
                    {{ domain().sslStatus === sslStatuses.WaitingForDns ? 'hourglass_empty' : 'security' }}
                  </mat-icon>
                  <span class="__badge-detail-section-title">
                    {{ translations().sslInstallation.title }} {{ domain().sslStatus === sslStatuses.WaitingForDns ? translations().sslStatuses[domain().sslStatus] : '' }}
                  </span>
                </div>

                @if (domain().sslStatus === sslStatuses.WaitingForDns) {
                  <div class="__badge-detail-info __badge-detail-info--waiting">
                    <p>{{ translations().sslInstallation.waitingForDns }}</p>
                    @if (domain().beingInstalledSslCertificate?.domainList?.length) {
                      <div class="__badge-detail-subsection">
                        <div class="__badge-detail-subsection-title">
                          {{ translations().detailLabels.domainList }}
                        </div>
                        <div class="__badge-detail-tag-list">
                          @for (d of domain().beingInstalledSslCertificate.domainList; track d) {
                            <span class="__badge-detail-tag">{{ d | middleEllipsis : 40 }}</span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }

                @if (domain().sslStatus === sslStatuses.BeingInstalled) {
                  <p class="__badge-detail-info">
                    {{ translations().sslInstallation.beingInstalled }}
                  </p>
                }

                @if (domain().sslStatus === sslStatuses.BeingRenewed) {
                  <p class="__badge-detail-info">
                    {{ translations().sslInstallation.beingRenewed }}
                  </p>
                }
              </div>

              <!-- Separator between installation and deployment -->
              <zui-separator
                [orientation]="'horizontal'"
                [size]="'small'"
                [spacing]="'small'"
                [position]="'start'" />
            }

            <!-- SSL Deployment Section (BELOW installation) -->
            @if (showSslDeployment()) {
              <div class="__ssl-deployment-section">
                <div
                  class="__badge-detail-section-header __badge-detail-section-header--ssl-deployment"
                  [class.is-active]="domain().deployedSslCertificate">
                  <mat-icon class="__badge-detail-section-icon">verified</mat-icon>
                  <span class="__badge-detail-section-title">
                    {{ translations().sslDeployment.title }} {{ domain().deployedSslCertificate
                       ? translations().deploymentStatuses.deployed
                       : translations().deploymentStatuses.notDeployed }}
                  </span>
                </div>

                @if (domain().deployedSslCertificate) {
                  <!-- Valid From and Valid Till (same style as DNS check times) -->
                  <div class="__badge-detail-checks-row">
                    <div class="__badge-detail-check-item">
                      <span class="__badge-detail-check-label">
                        {{ translations().detailLabels.validFrom }}
                      </span>
                      <span class="__badge-detail-check-value">
                        {{ domain().deployedSslCertificate.validFrom | date:'medium' }}
                      </span>
                    </div>
                    <div class="__badge-detail-check-item">
                      <span class="__badge-detail-check-label">
                        {{ translations().detailLabels.validTill }}
                      </span>
                      <span class="__badge-detail-check-value">
                        {{ domain().deployedSslCertificate.validTill | date:'medium' }}
                      </span>
                    </div>
                  </div>

                  <!-- Registered Domain (same style as DNS subsections) -->
                  <div class="__badge-detail-subsection">
                    <div class="__badge-detail-subsection-title">
                      {{ translations().detailLabels.registeredDomain }}
                    </div>
                    <div class="__badge-detail-tag-list">
                      <span class="__badge-detail-tag">{{ domain().deployedSslCertificate.registeredDomain | middleEllipsis : 40 }}</span>
                    </div>
                  </div>
                  @if (domain().deployedSslCertificate.domainList?.length) {
                    <div class="__badge-detail-subsection">
                      <div class="__badge-detail-subsection-title">
                        {{ translations().detailLabels.domainList }}
                      </div>
                      <div class="__badge-detail-tag-list">
                        @for (d of domain().deployedSslCertificate.domainList; track d) {
                          <span class="__badge-detail-tag">{{ d | middleEllipsis : 40 }}</span>
                        }
                      </div>
                    </div>
                  }
                } @else {
                  <p class="__badge-detail-info">
                    {{ translations().sslDeployment.notDeployedInfo }}
                  </p>
                }
              </div>
            }

          </div>
        }

        <!-- CDN Badge Popover -->
        @case ('cdn') {
          <div class="__badge-detail-section">

            <div class="__badge-detail-row">
              <span class="__badge-detail-label">{{ translations().cdnInfo.statusLabel }}</span>
              <span
                class="__badge-detail-value"
                [class.is-active]="domain().cdnStatus === cdnStatuses.Active">
                {{ translations().cdnStatuses[domain().cdnStatus] }}
              </span>
            </div>

            <p class="__badge-detail-info">
              @switch (domain().cdnStatus) {
                @case (cdnStatuses.BeingInstalled) {
                  {{ translations().cdnInfo.beingInstalled }}
                }
                @case (cdnStatuses.Active) {
                  {{ translations().cdnInfo.active }}
                }
                @case (cdnStatuses.BeingDisabled) {
                  {{ translations().cdnInfo.beingDisabled }}
                }
              }
            </p>

          </div>
        }

      }

    </div>
  </zef-scrollbar>

</mat-card>
